import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Building2,
  Users,
  Phone,
  Mail,
  MapPin,
  Plus,
  Search,
  Filter,
  ChevronDown,
  ChevronRight,
  Edit2,
  Trash2,
  X,
  Save,
  RefreshCw,
  Loader2,
  Globe,
  Tag,
  Calendar,
  DollarSign,
  Clock,
  CheckCircle,
  AlertCircle,
  User,
  MoreVertical,
  ExternalLink,
  SortAsc,
  SortDesc,
  Link2,
  Unlink,
  Network,
  Car
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Priority colors
const PRIORITY_CONFIG = {
  urgent: { color: 'text-red-400', bg: 'bg-red-500/20', label: 'Urgent' },
  high: { color: 'text-orange-400', bg: 'bg-orange-500/20', label: 'High' },
  medium: { color: 'text-blue-400', bg: 'bg-blue-500/20', label: 'Medium' },
  low: { color: 'text-slate-400', bg: 'bg-slate-500/20', label: 'Low' }
};

// Status colors
const STATUS_CONFIG = {
  new: { color: 'text-slate-400', bg: 'bg-slate-500/20', label: 'New' },
  contacted: { color: 'text-blue-400', bg: 'bg-blue-500/20', label: 'Contacted' },
  qualified: { color: 'text-purple-400', bg: 'bg-purple-500/20', label: 'Qualified' },
  proposal: { color: 'text-amber-400', bg: 'bg-amber-500/20', label: 'Proposal' },
  negotiation: { color: 'text-orange-400', bg: 'bg-orange-500/20', label: 'Negotiation' },
  won: { color: 'text-green-400', bg: 'bg-green-500/20', label: 'Won' },
  lost: { color: 'text-red-400', bg: 'bg-red-500/20', label: 'Lost' }
};

// Contact Card Component
const ContactCard = ({ contact, onEdit, onDelete, onCall, onEmail }) => (
  <div className="p-3 bg-white/5 rounded-lg border border-white/10 hover:border-white/20 transition-colors">
    <div className="flex items-start justify-between">
      <div className="flex items-center space-x-3">
        <div className="w-10 h-10 rounded-full bg-neon-cyan/20 flex items-center justify-center">
          <User className="w-5 h-5 text-neon-cyan" />
        </div>
        <div>
          <div className="flex items-center space-x-2">
            <span className="font-medium text-white">{contact.name}</span>
            {contact.is_primary && (
              <span className="px-1.5 py-0.5 bg-neon-cyan/20 text-neon-cyan text-xs rounded">Primary</span>
            )}
          </div>
          {contact.title && <p className="text-xs text-slate-400">{contact.title}</p>}
        </div>
      </div>
      <div className="flex items-center space-x-1">
        <button onClick={() => onEdit(contact)} className="p-1 rounded hover:bg-white/10 text-slate-400 hover:text-white">
          <Edit2 className="w-3.5 h-3.5" />
        </button>
        <button onClick={() => onDelete(contact)} className="p-1 rounded hover:bg-red-500/20 text-slate-400 hover:text-red-400">
          <Trash2 className="w-3.5 h-3.5" />
        </button>
      </div>
    </div>
    <div className="mt-2 flex items-center space-x-4 text-sm">
      {contact.email && (
        <button 
          onClick={() => onEmail(contact.email)}
          className="flex items-center text-slate-400 hover:text-neon-cyan transition-colors"
        >
          <Mail className="w-3.5 h-3.5 mr-1" />
          <span className="truncate max-w-[150px]">{contact.email}</span>
        </button>
      )}
      {contact.phone && (
        <button 
          onClick={() => onCall(contact.phone)}
          className="flex items-center text-slate-400 hover:text-green-400 transition-colors"
        >
          <Phone className="w-3.5 h-3.5 mr-1" />
          {contact.phone}
        </button>
      )}
    </div>
  </div>
);

// Lead Row Component
const LeadRow = ({ lead, onView, onEdit, onDelete, onAssociations, expanded, onToggle }) => {
  const primaryContact = lead.contacts?.find(c => c.is_primary) || lead.contacts?.[0];
  const statusConfig = STATUS_CONFIG[lead.status] || STATUS_CONFIG.new;
  const priorityConfig = PRIORITY_CONFIG[lead.priority] || PRIORITY_CONFIG.medium;
  
  // Check if has any associations
  const hasParent = !!lead.parent_company_id;
  const hasChildren = lead.child_company_ids?.length > 0;
  const hasDrivers = lead.associated_driver_ids?.length > 0;
  const hasAssociations = hasParent || hasChildren || hasDrivers;

  return (
    <>
      <tr 
        className="border-b border-white/5 hover:bg-white/5 cursor-pointer transition-colors"
        onClick={() => onView(lead)}
      >
        {/* Expand toggle */}
        <td className="px-3 py-3 w-10" onClick={(e) => { e.stopPropagation(); onToggle(lead.id); }}>
          <button className="p-1 rounded hover:bg-white/10 text-slate-400">
            {expanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
          </button>
        </td>
        
        {/* Company */}
        <td className="px-3 py-3">
          <div className="flex items-center space-x-3">
            <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-neon-cyan/20 to-neon-purple/20 flex items-center justify-center flex-shrink-0">
              <Building2 className="w-4 h-4 text-neon-cyan" />
            </div>
            <div>
              <div className="font-medium text-white flex items-center space-x-2">
                <span>{lead.company_name}</span>
                {hasAssociations && (
                  <div className="flex items-center space-x-0.5">
                    {hasParent && <div className="w-1.5 h-1.5 rounded-full bg-neon-cyan" title="Has parent company" />}
                    {hasChildren && <div className="w-1.5 h-1.5 rounded-full bg-purple-400" title="Has child companies" />}
                    {hasDrivers && <div className="w-1.5 h-1.5 rounded-full bg-green-400" title="Has drivers" />}
                  </div>
                )}
              </div>
              <div className="text-xs text-slate-400">{lead.facility_type}</div>
            </div>
          </div>
        </td>
        
        {/* Primary Contact */}
        <td className="px-3 py-3">
          {primaryContact ? (
            <div>
              <div className="text-white text-sm">{primaryContact.name}</div>
              <div className="text-xs text-slate-400">{primaryContact.title || primaryContact.email}</div>
            </div>
          ) : (
            <span className="text-slate-500 text-sm">No contact</span>
          )}
        </td>
        
        {/* Location */}
        <td className="px-3 py-3">
          {lead.city || lead.state ? (
            <div className="text-sm text-slate-300">
              {[lead.city, lead.state].filter(Boolean).join(', ')}
            </div>
          ) : (
            <span className="text-slate-500 text-sm">-</span>
          )}
        </td>
        
        {/* Priority */}
        <td className="px-3 py-3">
          <span className={`px-2 py-1 rounded text-xs ${priorityConfig.bg} ${priorityConfig.color}`}>
            {priorityConfig.label}
          </span>
        </td>
        
        {/* Status */}
        <td className="px-3 py-3">
          <span className={`px-2 py-1 rounded text-xs ${statusConfig.bg} ${statusConfig.color}`}>
            {statusConfig.label}
          </span>
        </td>
        
        {/* Last Contact */}
        <td className="px-3 py-3 text-sm text-slate-400">
          {lead.last_contact_date 
            ? new Date(lead.last_contact_date).toLocaleDateString() 
            : <span className="text-slate-500">Never</span>
          }
        </td>
        
        {/* Actions */}
        <td className="px-3 py-3 text-right" onClick={(e) => e.stopPropagation()}>
          <div className="flex items-center justify-end space-x-1">
            <button
              onClick={() => onAssociations(lead)}
              className="p-1.5 rounded hover:bg-neon-cyan/20 text-slate-400 hover:text-neon-cyan"
              title="Association Links"
            >
              <Network className="w-4 h-4" />
            </button>
            <button
              onClick={() => onEdit(lead)}
              className="p-1.5 rounded hover:bg-white/10 text-slate-400 hover:text-white"
              title="Edit"
            >
              <Edit2 className="w-4 h-4" />
            </button>
            <button
              onClick={() => onDelete(lead)}
              className="p-1.5 rounded hover:bg-red-500/20 text-slate-400 hover:text-red-400"
              title="Delete"
            >
              <Trash2 className="w-4 h-4" />
            </button>
          </div>
        </td>
      </tr>
      
      {/* Expanded contacts */}
      {expanded && lead.contacts?.length > 0 && (
        <tr>
          <td colSpan={8} className="px-3 py-3 bg-white/5">
            <div className="ml-12 grid grid-cols-2 lg:grid-cols-3 gap-3">
              {lead.contacts.map((contact, idx) => (
                <div key={contact.id || idx} className="p-3 bg-slate-800/50 rounded-lg">
                  <div className="flex items-center space-x-2 mb-2">
                    <User className="w-4 h-4 text-slate-400" />
                    <span className="text-white text-sm font-medium">{contact.name}</span>
                    {contact.is_primary && (
                      <span className="px-1 py-0.5 bg-neon-cyan/20 text-neon-cyan text-xs rounded">Primary</span>
                    )}
                  </div>
                  {contact.title && <p className="text-xs text-slate-400 mb-1">{contact.title}</p>}
                  <div className="flex flex-wrap gap-2 text-xs">
                    {contact.email && (
                      <a href={`mailto:${contact.email}`} className="text-neon-cyan hover:underline flex items-center">
                        <Mail className="w-3 h-3 mr-1" />{contact.email}
                      </a>
                    )}
                    {contact.phone && (
                      <a href={`tel:${contact.phone}`} className="text-green-400 hover:underline flex items-center">
                        <Phone className="w-3 h-3 mr-1" />{contact.phone}
                      </a>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </td>
        </tr>
      )}
    </>
  );
};

// Lead Form Modal
const LeadFormModal = ({ isOpen, onClose, lead, facilityTypes, opportunityTypes, onSave }) => {
  const [form, setForm] = useState({
    company_name: '',
    facility_type: 'Other',
    address: '',
    city: '',
    state: '',
    zip_code: '',
    website: '',
    status: 'new',
    priority: 'medium',
    opportunity_types: [],
    estimated_value: 0,
    source: '',
    notes: '',
    tags: [],
    contacts: []
  });
  const [newContact, setNewContact] = useState({ name: '', title: '', email: '', phone: '', is_primary: false });
  const [showAddContact, setShowAddContact] = useState(false);
  const [newFacilityType, setNewFacilityType] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (lead) {
      setForm({
        company_name: lead.company_name || '',
        facility_type: lead.facility_type || 'Other',
        address: lead.address || '',
        city: lead.city || '',
        state: lead.state || '',
        zip_code: lead.zip_code || '',
        website: lead.website || '',
        status: lead.status || 'new',
        priority: lead.priority || 'medium',
        opportunity_types: lead.opportunity_types || [],
        estimated_value: lead.estimated_value || 0,
        source: lead.source || '',
        notes: lead.notes || '',
        tags: lead.tags || [],
        contacts: lead.contacts || []
      });
    } else {
      setForm({
        company_name: '',
        facility_type: 'Other',
        address: '',
        city: '',
        state: '',
        zip_code: '',
        website: '',
        status: 'new',
        priority: 'medium',
        opportunity_types: [],
        estimated_value: 0,
        source: '',
        notes: '',
        tags: [],
        contacts: []
      });
    }
  }, [lead, isOpen]);

  const handleAddContact = () => {
    if (!newContact.name.trim()) {
      toast.error('Contact name is required');
      return;
    }
    const contact = { ...newContact, id: `contact_${Date.now()}` };
    setForm({ ...form, contacts: [...form.contacts, contact] });
    setNewContact({ name: '', title: '', email: '', phone: '', is_primary: false });
    setShowAddContact(false);
  };

  const handleRemoveContact = (contactId) => {
    setForm({ ...form, contacts: form.contacts.filter(c => c.id !== contactId) });
  };

  const handleAddFacilityType = async () => {
    if (!newFacilityType.trim()) return;
    try {
      await fetch(`${API_URL}/api/crm-leads/facility-types`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: newFacilityType })
      });
      setForm({ ...form, facility_type: newFacilityType });
      setNewFacilityType('');
      toast.success('Facility type added');
    } catch (error) {
      toast.error('Failed to add facility type');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.company_name.trim()) {
      toast.error('Company name is required');
      return;
    }
    setSaving(true);
    try {
      await onSave(form, lead?.id);
      onClose();
    } finally {
      setSaving(false);
    }
  };

  const toggleOpportunityType = (type) => {
    const types = form.opportunity_types.includes(type)
      ? form.opportunity_types.filter(t => t !== type)
      : [...form.opportunity_types, type];
    setForm({ ...form, opportunity_types: types });
  };

  if (!isOpen) return null;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4 overflow-y-auto"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.95, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.95, opacity: 0 }}
        className="bg-slate-800 rounded-2xl w-full max-w-3xl max-h-[90vh] overflow-hidden border border-white/10 my-8"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between p-4 border-b border-white/10">
          <h3 className="text-lg font-bold text-white">
            {lead ? 'Edit Lead' : 'Add New Lead'}
          </h3>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-white/10 text-slate-400">
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-4 overflow-y-auto max-h-[calc(90vh-140px)]">
          <div className="space-y-6">
            {/* Company Info Section */}
            <div>
              <h4 className="text-sm font-medium text-slate-300 mb-3 flex items-center">
                <Building2 className="w-4 h-4 mr-2 text-neon-cyan" />
                Company Information
              </h4>
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2">
                  <label className="block text-xs text-slate-400 mb-1">Company Name *</label>
                  <input
                    type="text"
                    value={form.company_name}
                    onChange={(e) => setForm({ ...form, company_name: e.target.value })}
                    className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white focus:border-neon-cyan/50 focus:outline-none"
                    placeholder="ABC Healthcare"
                    required
                  />
                </div>
                <div>
                  <label className="block text-xs text-slate-400 mb-1">Facility Type</label>
                  <div className="flex space-x-2">
                    <select
                      value={form.facility_type}
                      onChange={(e) => setForm({ ...form, facility_type: e.target.value })}
                      className="flex-1 px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white focus:border-neon-cyan/50 focus:outline-none"
                    >
                      {facilityTypes.map(type => (
                        <option key={type} value={type}>{type}</option>
                      ))}
                    </select>
                  </div>
                  {/* Add new facility type */}
                  <div className="flex items-center space-x-2 mt-2">
                    <input
                      type="text"
                      value={newFacilityType}
                      onChange={(e) => setNewFacilityType(e.target.value)}
                      className="flex-1 px-2 py-1 bg-white/5 border border-white/10 rounded text-white text-sm"
                      placeholder="Add new type..."
                    />
                    <button
                      type="button"
                      onClick={handleAddFacilityType}
                      className="px-2 py-1 bg-neon-cyan/20 text-neon-cyan rounded text-sm hover:bg-neon-cyan/30"
                    >
                      Add
                    </button>
                  </div>
                </div>
                <div>
                  <label className="block text-xs text-slate-400 mb-1">Website</label>
                  <input
                    type="url"
                    value={form.website}
                    onChange={(e) => setForm({ ...form, website: e.target.value })}
                    className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white focus:border-neon-cyan/50 focus:outline-none"
                    placeholder="https://..."
                  />
                </div>
                <div className="col-span-2">
                  <label className="block text-xs text-slate-400 mb-1">Address</label>
                  <input
                    type="text"
                    value={form.address}
                    onChange={(e) => setForm({ ...form, address: e.target.value })}
                    className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white focus:border-neon-cyan/50 focus:outline-none"
                    placeholder="123 Main St"
                  />
                </div>
                <div>
                  <label className="block text-xs text-slate-400 mb-1">City</label>
                  <input
                    type="text"
                    value={form.city}
                    onChange={(e) => setForm({ ...form, city: e.target.value })}
                    className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white focus:border-neon-cyan/50 focus:outline-none"
                  />
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <label className="block text-xs text-slate-400 mb-1">State</label>
                    <input
                      type="text"
                      value={form.state}
                      onChange={(e) => setForm({ ...form, state: e.target.value })}
                      className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white focus:border-neon-cyan/50 focus:outline-none"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-slate-400 mb-1">ZIP</label>
                    <input
                      type="text"
                      value={form.zip_code}
                      onChange={(e) => setForm({ ...form, zip_code: e.target.value })}
                      className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white focus:border-neon-cyan/50 focus:outline-none"
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Contacts Section */}
            <div>
              <div className="flex items-center justify-between mb-3">
                <h4 className="text-sm font-medium text-slate-300 flex items-center">
                  <Users className="w-4 h-4 mr-2 text-neon-cyan" />
                  Contacts
                </h4>
                <button
                  type="button"
                  onClick={() => setShowAddContact(true)}
                  className="text-xs text-neon-cyan hover:text-neon-cyan/80 flex items-center"
                >
                  <Plus className="w-3 h-3 mr-1" /> Add Contact
                </button>
              </div>
              
              {/* Existing contacts */}
              {form.contacts.length > 0 ? (
                <div className="space-y-2 mb-3">
                  {form.contacts.map((contact, idx) => (
                    <div key={contact.id || idx} className="flex items-center justify-between p-2 bg-white/5 rounded-lg">
                      <div className="flex items-center space-x-3">
                        <User className="w-4 h-4 text-slate-400" />
                        <div>
                          <span className="text-white text-sm">{contact.name}</span>
                          {contact.is_primary && <span className="ml-2 text-xs text-neon-cyan">(Primary)</span>}
                          {contact.title && <span className="ml-2 text-xs text-slate-400">- {contact.title}</span>}
                        </div>
                      </div>
                      <div className="flex items-center space-x-2 text-xs text-slate-400">
                        {contact.email && <span>{contact.email}</span>}
                        {contact.phone && <span>{contact.phone}</span>}
                        <button
                          type="button"
                          onClick={() => handleRemoveContact(contact.id)}
                          className="p-1 hover:text-red-400"
                        >
                          <X className="w-3 h-3" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-slate-500 mb-3">No contacts added yet</p>
              )}
              
              {/* Add contact form */}
              {showAddContact && (
                <div className="p-3 bg-white/5 rounded-lg border border-white/10 space-y-3">
                  <div className="grid grid-cols-2 gap-3">
                    <input
                      type="text"
                      value={newContact.name}
                      onChange={(e) => setNewContact({ ...newContact, name: e.target.value })}
                      className="px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white text-sm"
                      placeholder="Contact Name *"
                    />
                    <input
                      type="text"
                      value={newContact.title}
                      onChange={(e) => setNewContact({ ...newContact, title: e.target.value })}
                      className="px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white text-sm"
                      placeholder="Title/Role"
                    />
                    <input
                      type="email"
                      value={newContact.email}
                      onChange={(e) => setNewContact({ ...newContact, email: e.target.value })}
                      className="px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white text-sm"
                      placeholder="Email"
                    />
                    <input
                      type="tel"
                      value={newContact.phone}
                      onChange={(e) => setNewContact({ ...newContact, phone: e.target.value })}
                      className="px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white text-sm"
                      placeholder="Phone"
                    />
                  </div>
                  <div className="flex items-center justify-between">
                    <label className="flex items-center text-sm text-slate-300">
                      <input
                        type="checkbox"
                        checked={newContact.is_primary}
                        onChange={(e) => setNewContact({ ...newContact, is_primary: e.target.checked })}
                        className="mr-2"
                      />
                      Primary Contact
                    </label>
                    <div className="flex space-x-2">
                      <button
                        type="button"
                        onClick={() => setShowAddContact(false)}
                        className="px-3 py-1 text-sm text-slate-400 hover:text-white"
                      >
                        Cancel
                      </button>
                      <button
                        type="button"
                        onClick={handleAddContact}
                        className="px-3 py-1 bg-neon-cyan text-slate-900 rounded text-sm"
                      >
                        Add
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Status & Priority */}
            <div>
              <h4 className="text-sm font-medium text-slate-300 mb-3 flex items-center">
                <Tag className="w-4 h-4 mr-2 text-neon-cyan" />
                Status & Priority
              </h4>
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="block text-xs text-slate-400 mb-1">Status</label>
                  <select
                    value={form.status}
                    onChange={(e) => setForm({ ...form, status: e.target.value })}
                    className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white focus:border-neon-cyan/50 focus:outline-none"
                  >
                    {Object.entries(STATUS_CONFIG).map(([key, config]) => (
                      <option key={key} value={key}>{config.label}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-xs text-slate-400 mb-1">Priority</label>
                  <select
                    value={form.priority}
                    onChange={(e) => setForm({ ...form, priority: e.target.value })}
                    className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white focus:border-neon-cyan/50 focus:outline-none"
                  >
                    {Object.entries(PRIORITY_CONFIG).map(([key, config]) => (
                      <option key={key} value={key}>{config.label}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-xs text-slate-400 mb-1">Est. Value ($)</label>
                  <input
                    type="number"
                    value={form.estimated_value}
                    onChange={(e) => setForm({ ...form, estimated_value: parseFloat(e.target.value) || 0 })}
                    className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white focus:border-neon-cyan/50 focus:outline-none"
                  />
                </div>
              </div>
            </div>

            {/* Opportunity Types */}
            <div>
              <h4 className="text-sm font-medium text-slate-300 mb-3">Opportunity Types (What are we selling?)</h4>
              <div className="flex flex-wrap gap-2">
                {opportunityTypes.map(type => (
                  <button
                    key={type}
                    type="button"
                    onClick={() => toggleOpportunityType(type)}
                    className={`px-3 py-1.5 rounded-lg text-sm transition-colors ${
                      form.opportunity_types.includes(type)
                        ? 'bg-neon-cyan/20 text-neon-cyan border border-neon-cyan/50'
                        : 'bg-white/5 text-slate-400 border border-white/10 hover:border-white/20'
                    }`}
                  >
                    {type}
                  </button>
                ))}
              </div>
            </div>

            {/* Notes */}
            <div>
              <label className="block text-xs text-slate-400 mb-1">Notes</label>
              <textarea
                value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
                className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white focus:border-neon-cyan/50 focus:outline-none resize-none"
                rows={3}
                placeholder="Additional notes..."
              />
            </div>
          </div>

          {/* Actions */}
          <div className="flex justify-end space-x-3 mt-6 pt-4 border-t border-white/10">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 bg-white/10 hover:bg-white/20 rounded-lg text-white"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              className="px-4 py-2 bg-neon-cyan text-slate-900 rounded-lg font-medium hover:bg-neon-cyan/80 flex items-center space-x-2"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
              <span>{lead ? 'Update' : 'Create'} Lead</span>
            </button>
          </div>
        </form>
      </motion.div>
    </motion.div>
  );
};

// Association Links Modal
const AssociationLinksModal = ({ isOpen, onClose, lead, allLeads, onRefresh }) => {
  const [associations, setAssociations] = useState(null);
  const [availableCompanies, setAvailableCompanies] = useState([]);
  const [availableDrivers, setAvailableDrivers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('parent');
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    if (isOpen && lead) {
      fetchAssociations();
      fetchAvailable();
    }
  }, [isOpen, lead]);

  const fetchAssociations = async () => {
    try {
      const res = await fetch(`${API_URL}/api/crm-leads/${lead.id}/associations`);
      if (res.ok) {
        const data = await res.json();
        setAssociations(data);
      }
    } catch (error) {
      console.error('Failed to fetch associations:', error);
    }
  };

  const fetchAvailable = async () => {
    setLoading(true);
    try {
      const [companiesRes, driversRes] = await Promise.all([
        fetch(`${API_URL}/api/crm-leads/${lead.id}/available-links`),
        fetch(`${API_URL}/api/crm-leads/drivers/available`)
      ]);
      
      if (companiesRes.ok) {
        const data = await companiesRes.json();
        setAvailableCompanies(data.available_companies || []);
      }
      if (driversRes.ok) {
        const data = await driversRes.json();
        setAvailableDrivers(data.drivers || []);
      }
    } catch (error) {
      console.error('Failed to fetch available links:', error);
    } finally {
      setLoading(false);
    }
  };

  const linkParent = async (parentId) => {
    try {
      const res = await fetch(`${API_URL}/api/crm-leads/${lead.id}/link-parent/${parentId}`, {
        method: 'POST'
      });
      if (res.ok) {
        toast.success('Parent company linked');
        await fetchAssociations();
        await fetchAvailable();
        onRefresh();
      }
    } catch (error) {
      toast.error('Failed to link parent');
    }
  };

  const unlinkParent = async () => {
    try {
      const res = await fetch(`${API_URL}/api/crm-leads/${lead.id}/unlink-parent`, {
        method: 'DELETE'
      });
      if (res.ok) {
        toast.success('Parent company unlinked');
        await fetchAssociations();
        await fetchAvailable();
        onRefresh();
      }
    } catch (error) {
      toast.error('Failed to unlink parent');
    }
  };

  const linkChild = async (childId) => {
    try {
      const res = await fetch(`${API_URL}/api/crm-leads/${childId}/link-parent/${lead.id}`, {
        method: 'POST'
      });
      if (res.ok) {
        toast.success('Child company linked');
        await fetchAssociations();
        await fetchAvailable();
        onRefresh();
      }
    } catch (error) {
      toast.error('Failed to link child');
    }
  };

  const unlinkChild = async (childId) => {
    try {
      const res = await fetch(`${API_URL}/api/crm-leads/${childId}/unlink-parent`, {
        method: 'DELETE'
      });
      if (res.ok) {
        toast.success('Child company unlinked');
        await fetchAssociations();
        await fetchAvailable();
        onRefresh();
      }
    } catch (error) {
      toast.error('Failed to unlink child');
    }
  };

  const linkDriver = async (driverId) => {
    try {
      const res = await fetch(`${API_URL}/api/crm-leads/${lead.id}/link-driver/${driverId}`, {
        method: 'POST'
      });
      if (res.ok) {
        toast.success('Driver associated');
        await fetchAssociations();
        onRefresh();
      }
    } catch (error) {
      toast.error('Failed to associate driver');
    }
  };

  const unlinkDriver = async (driverId) => {
    try {
      const res = await fetch(`${API_URL}/api/crm-leads/${lead.id}/unlink-driver/${driverId}`, {
        method: 'DELETE'
      });
      if (res.ok) {
        toast.success('Driver unlinked');
        await fetchAssociations();
        onRefresh();
      }
    } catch (error) {
      toast.error('Failed to unlink driver');
    }
  };

  const filteredCompanies = availableCompanies.filter(c =>
    c.company_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    c.facility_type?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const filteredDrivers = availableDrivers.filter(d =>
    d.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    d.email?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (!isOpen || !lead) return null;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.95, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.95, opacity: 0 }}
        className="bg-slate-800 rounded-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden border border-white/10"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-white/10">
          <div>
            <h3 className="text-lg font-bold text-white flex items-center">
              <Network className="w-5 h-5 mr-2 text-neon-cyan" />
              Association Links
            </h3>
            <p className="text-sm text-slate-400 mt-1">
              Manage relationships for {lead.company_name}
            </p>
          </div>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-white/10 text-slate-400">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-white/10">
          {[
            { id: 'parent', label: 'Parent Company', icon: Building2 },
            { id: 'children', label: 'Child Companies', icon: Network },
            { id: 'drivers', label: 'Drivers', icon: Car }
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => { setActiveTab(tab.id); setSearchQuery(''); }}
              className={`flex-1 px-4 py-3 text-sm font-medium flex items-center justify-center space-x-2 transition-colors ${
                activeTab === tab.id
                  ? 'text-neon-cyan border-b-2 border-neon-cyan bg-neon-cyan/5'
                  : 'text-slate-400 hover:text-white hover:bg-white/5'
              }`}
            >
              <tab.icon className="w-4 h-4" />
              <span>{tab.label}</span>
            </button>
          ))}
        </div>

        {/* Content */}
        <div className="p-4 overflow-y-auto max-h-[calc(90vh-180px)]">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 text-neon-cyan animate-spin" />
            </div>
          ) : (
            <>
              {/* Parent Company Tab */}
              {activeTab === 'parent' && (
                <div className="space-y-4">
                  {/* Current Parent */}
                  {associations?.parent_company ? (
                    <div className="glass-card p-4">
                      <h4 className="text-sm font-medium text-slate-300 mb-3">Current Parent Company</h4>
                      <div className="flex items-center justify-between p-3 bg-neon-cyan/10 rounded-lg border border-neon-cyan/30">
                        <div className="flex items-center space-x-3">
                          <Building2 className="w-5 h-5 text-neon-cyan" />
                          <div>
                            <div className="font-medium text-white">{associations.parent_company.company_name}</div>
                            <div className="text-xs text-slate-400">{associations.parent_company.facility_type}</div>
                          </div>
                        </div>
                        <button
                          onClick={unlinkParent}
                          className="px-3 py-1.5 bg-red-500/20 hover:bg-red-500/30 text-red-400 rounded-lg text-sm flex items-center space-x-1"
                        >
                          <Unlink className="w-3 h-3" />
                          <span>Unlink</span>
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="glass-card p-4">
                      <h4 className="text-sm font-medium text-slate-300 mb-3">Link Parent Company</h4>
                      
                      {/* Search */}
                      <div className="relative mb-4">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                        <input
                          type="text"
                          value={searchQuery}
                          onChange={(e) => setSearchQuery(e.target.value)}
                          placeholder="Search companies..."
                          className="w-full pl-10 pr-4 py-2 bg-white/5 border border-white/10 rounded-lg text-white text-sm focus:border-neon-cyan/50 focus:outline-none"
                        />
                      </div>

                      {/* Available companies */}
                      <div className="space-y-2 max-h-[300px] overflow-y-auto">
                        {filteredCompanies.length === 0 ? (
                          <p className="text-center text-slate-500 py-4">No available companies</p>
                        ) : (
                          filteredCompanies.map(company => (
                            <div
                              key={company.id}
                              className="flex items-center justify-between p-3 bg-white/5 rounded-lg hover:bg-white/10 transition-colors"
                            >
                              <div className="flex items-center space-x-3">
                                <Building2 className="w-4 h-4 text-slate-400" />
                                <div>
                                  <div className="text-white text-sm">{company.company_name}</div>
                                  <div className="text-xs text-slate-400">
                                    {company.facility_type} {company.city && `· ${company.city}, ${company.state}`}
                                  </div>
                                </div>
                              </div>
                              <button
                                onClick={() => linkParent(company.id)}
                                className="px-3 py-1.5 bg-neon-cyan/20 hover:bg-neon-cyan/30 text-neon-cyan rounded-lg text-sm flex items-center space-x-1"
                              >
                                <Link2 className="w-3 h-3" />
                                <span>Link</span>
                              </button>
                            </div>
                          ))
                        )}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Child Companies Tab */}
              {activeTab === 'children' && (
                <div className="space-y-4">
                  {/* Current Children */}
                  {associations?.child_companies?.length > 0 && (
                    <div className="glass-card p-4">
                      <h4 className="text-sm font-medium text-slate-300 mb-3">
                        Current Child Companies ({associations.child_companies.length})
                      </h4>
                      <div className="space-y-2">
                        {associations.child_companies.map(child => (
                          <div
                            key={child.id}
                            className="flex items-center justify-between p-3 bg-purple-500/10 rounded-lg border border-purple-500/30"
                          >
                            <div className="flex items-center space-x-3">
                              <Building2 className="w-4 h-4 text-purple-400" />
                              <div>
                                <div className="text-white text-sm">{child.company_name}</div>
                                <div className="text-xs text-slate-400">{child.facility_type}</div>
                              </div>
                            </div>
                            <button
                              onClick={() => unlinkChild(child.id)}
                              className="px-3 py-1.5 bg-red-500/20 hover:bg-red-500/30 text-red-400 rounded-lg text-sm flex items-center space-x-1"
                            >
                              <Unlink className="w-3 h-3" />
                              <span>Unlink</span>
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Add Child */}
                  <div className="glass-card p-4">
                    <h4 className="text-sm font-medium text-slate-300 mb-3">Add Child Company</h4>
                    
                    {/* Search */}
                    <div className="relative mb-4">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                      <input
                        type="text"
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        placeholder="Search companies..."
                        className="w-full pl-10 pr-4 py-2 bg-white/5 border border-white/10 rounded-lg text-white text-sm focus:border-neon-cyan/50 focus:outline-none"
                      />
                    </div>

                    {/* Available companies */}
                    <div className="space-y-2 max-h-[300px] overflow-y-auto">
                      {filteredCompanies.length === 0 ? (
                        <p className="text-center text-slate-500 py-4">No available companies</p>
                      ) : (
                        filteredCompanies.map(company => (
                          <div
                            key={company.id}
                            className="flex items-center justify-between p-3 bg-white/5 rounded-lg hover:bg-white/10 transition-colors"
                          >
                            <div className="flex items-center space-x-3">
                              <Building2 className="w-4 h-4 text-slate-400" />
                              <div>
                                <div className="text-white text-sm">{company.company_name}</div>
                                <div className="text-xs text-slate-400">
                                  {company.facility_type} {company.city && `· ${company.city}, ${company.state}`}
                                </div>
                              </div>
                            </div>
                            <button
                              onClick={() => linkChild(company.id)}
                              className="px-3 py-1.5 bg-purple-500/20 hover:bg-purple-500/30 text-purple-400 rounded-lg text-sm flex items-center space-x-1"
                            >
                              <Link2 className="w-3 h-3" />
                              <span>Add</span>
                            </button>
                          </div>
                        ))
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* Drivers Tab */}
              {activeTab === 'drivers' && (
                <div className="space-y-4">
                  {/* Current Drivers */}
                  {associations?.associated_drivers?.length > 0 && (
                    <div className="glass-card p-4">
                      <h4 className="text-sm font-medium text-slate-300 mb-3">
                        Associated Drivers ({associations.associated_drivers.length})
                      </h4>
                      <div className="space-y-2">
                        {associations.associated_drivers.map(driver => (
                          <div
                            key={driver.id}
                            className="flex items-center justify-between p-3 bg-green-500/10 rounded-lg border border-green-500/30"
                          >
                            <div className="flex items-center space-x-3">
                              <Car className="w-4 h-4 text-green-400" />
                              <div>
                                <div className="text-white text-sm">{driver.name}</div>
                                <div className="text-xs text-slate-400">
                                  {driver.email} {driver.phone && `· ${driver.phone}`}
                                </div>
                              </div>
                            </div>
                            <button
                              onClick={() => unlinkDriver(driver.id)}
                              className="px-3 py-1.5 bg-red-500/20 hover:bg-red-500/30 text-red-400 rounded-lg text-sm flex items-center space-x-1"
                            >
                              <Unlink className="w-3 h-3" />
                              <span>Unlink</span>
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Add Driver */}
                  <div className="glass-card p-4">
                    <h4 className="text-sm font-medium text-slate-300 mb-3">Associate Driver</h4>
                    
                    {/* Search */}
                    <div className="relative mb-4">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                      <input
                        type="text"
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        placeholder="Search drivers..."
                        className="w-full pl-10 pr-4 py-2 bg-white/5 border border-white/10 rounded-lg text-white text-sm focus:border-neon-cyan/50 focus:outline-none"
                      />
                    </div>

                    {/* Available drivers */}
                    <div className="space-y-2 max-h-[300px] overflow-y-auto">
                      {availableDrivers.length === 0 ? (
                        <p className="text-center text-slate-500 py-4">
                          No drivers in system. Add drivers via the Driver Portal first.
                        </p>
                      ) : filteredDrivers.length === 0 ? (
                        <p className="text-center text-slate-500 py-4">No matching drivers</p>
                      ) : (
                        filteredDrivers
                          .filter(d => !associations?.associated_drivers?.some(ad => ad.id === d.id))
                          .map(driver => (
                            <div
                              key={driver.id}
                              className="flex items-center justify-between p-3 bg-white/5 rounded-lg hover:bg-white/10 transition-colors"
                            >
                              <div className="flex items-center space-x-3">
                                <Car className="w-4 h-4 text-slate-400" />
                                <div>
                                  <div className="text-white text-sm">{driver.name}</div>
                                  <div className="text-xs text-slate-400">
                                    {driver.email} {driver.status && `· ${driver.status}`}
                                  </div>
                                </div>
                              </div>
                              <button
                                onClick={() => linkDriver(driver.id)}
                                className="px-3 py-1.5 bg-green-500/20 hover:bg-green-500/30 text-green-400 rounded-lg text-sm flex items-center space-x-1"
                              >
                                <Link2 className="w-3 h-3" />
                                <span>Associate</span>
                              </button>
                            </div>
                          ))
                      )}
                    </div>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </motion.div>
    </motion.div>
  );
};

// Main Component
export const LeadsCRM = () => {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [facilityTypes, setFacilityTypes] = useState([]);
  const [opportunityTypes, setOpportunityTypes] = useState([]);
  const [stats, setStats] = useState(null);
  
  // Filters & Sort
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterPriority, setFilterPriority] = useState('');
  const [filterFacilityType, setFilterFacilityType] = useState('');
  const [sortBy, setSortBy] = useState('company_name');
  const [sortOrder, setSortOrder] = useState('asc');
  
  // Modal states
  const [showForm, setShowForm] = useState(false);
  const [selectedLead, setSelectedLead] = useState(null);
  const [expandedRows, setExpandedRows] = useState(new Set());
  const [showAssociations, setShowAssociations] = useState(false);
  const [associationLead, setAssociationLead] = useState(null);

  // Fetch leads
  const fetchLeads = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (filterStatus) params.append('status', filterStatus);
      if (filterPriority) params.append('priority', filterPriority);
      if (filterFacilityType) params.append('facility_type', filterFacilityType);
      if (searchQuery) params.append('search', searchQuery);
      params.append('sort_by', sortBy);
      params.append('sort_order', sortOrder);

      const res = await fetch(`${API_URL}/api/crm-leads?${params}`);
      if (res.ok) {
        const data = await res.json();
        setLeads(data.leads || []);
      }
    } catch (error) {
      console.error('Failed to fetch leads:', error);
    }
  }, [filterStatus, filterPriority, filterFacilityType, searchQuery, sortBy, sortOrder]);

  // Fetch facility types
  const fetchFacilityTypes = async () => {
    try {
      const res = await fetch(`${API_URL}/api/crm-leads/facility-types`);
      if (res.ok) {
        const data = await res.json();
        setFacilityTypes(data.facility_types || []);
      }
    } catch (error) {
      console.error('Failed to fetch facility types:', error);
    }
  };

  // Fetch opportunity types
  const fetchOpportunityTypes = async () => {
    try {
      const res = await fetch(`${API_URL}/api/crm-leads/opportunity-types`);
      if (res.ok) {
        const data = await res.json();
        setOpportunityTypes(data.opportunity_types || []);
      }
    } catch (error) {
      console.error('Failed to fetch opportunity types:', error);
    }
  };

  // Fetch stats
  const fetchStats = async () => {
    try {
      const res = await fetch(`${API_URL}/api/crm-leads/stats/summary`);
      if (res.ok) {
        const data = await res.json();
        setStats(data);
      }
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    }
  };

  // Initial load
  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([fetchLeads(), fetchFacilityTypes(), fetchOpportunityTypes(), fetchStats()]);
      setLoading(false);
    };
    loadData();
  }, [fetchLeads]);

  // Save lead
  const handleSaveLead = async (formData, leadId) => {
    try {
      const url = leadId ? `${API_URL}/api/crm-leads/${leadId}` : `${API_URL}/api/crm-leads`;
      const method = leadId ? 'PUT' : 'POST';

      const res = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });

      if (res.ok) {
        toast.success(leadId ? 'Lead updated' : 'Lead created');
        await fetchLeads();
        await fetchStats();
      } else {
        throw new Error('Failed to save');
      }
    } catch (error) {
      toast.error('Failed to save lead');
      throw error;
    }
  };

  // Delete lead
  const handleDeleteLead = async (lead) => {
    if (!confirm(`Delete ${lead.company_name}?`)) return;
    
    try {
      const res = await fetch(`${API_URL}/api/crm-leads/${lead.id}`, { method: 'DELETE' });
      if (res.ok) {
        toast.success('Lead deleted');
        await fetchLeads();
        await fetchStats();
      }
    } catch (error) {
      toast.error('Failed to delete lead');
    }
  };

  // Toggle row expansion
  const toggleRow = (leadId) => {
    const newExpanded = new Set(expandedRows);
    if (newExpanded.has(leadId)) {
      newExpanded.delete(leadId);
    } else {
      newExpanded.add(leadId);
    }
    setExpandedRows(newExpanded);
  };

  // Toggle sort
  const toggleSort = (field) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortOrder('asc');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 text-neon-cyan animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="leads-crm">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-2xl font-heading font-bold text-white flex items-center">
            <Building2 className="w-6 h-6 mr-2 text-neon-cyan" />
            Leads
          </h1>
          <p className="text-slate-400 text-sm mt-1">Company-centric lead management</p>
        </div>
        <button
          onClick={() => { setSelectedLead(null); setShowForm(true); }}
          className="btn-primary flex items-center space-x-2"
        >
          <Plus className="w-4 h-4" />
          <span>Add Lead</span>
        </button>
      </div>

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
          <div className="glass-card p-4">
            <div className="text-2xl font-bold text-white">{stats.total_leads || 0}</div>
            <div className="text-xs text-slate-400">Total Leads</div>
          </div>
          <div className="glass-card p-4">
            <div className="text-2xl font-bold text-neon-green">${((stats.total_estimated_value || 0) / 1000).toFixed(0)}K</div>
            <div className="text-xs text-slate-400">Est. Value</div>
          </div>
          <div className="glass-card p-4">
            <div className="text-2xl font-bold text-blue-400">{stats.by_status?.new || 0}</div>
            <div className="text-xs text-slate-400">New Leads</div>
          </div>
          <div className="glass-card p-4">
            <div className="text-2xl font-bold text-amber-400">{stats.by_status?.proposal || 0}</div>
            <div className="text-xs text-slate-400">In Proposal</div>
          </div>
          <div className="glass-card p-4">
            <div className="text-2xl font-bold text-green-400">{stats.by_status?.won || 0}</div>
            <div className="text-xs text-slate-400">Won</div>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 max-w-xs">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search companies, contacts..."
            className="w-full pl-10 pr-4 py-2 bg-white/5 border border-white/10 rounded-lg text-white text-sm focus:border-neon-cyan/50 focus:outline-none"
          />
        </div>
        
        <select
          value={filterPriority}
          onChange={(e) => setFilterPriority(e.target.value)}
          className="px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white text-sm focus:border-neon-cyan/50 focus:outline-none"
        >
          <option value="">All Priorities</option>
          {Object.entries(PRIORITY_CONFIG).map(([key, config]) => (
            <option key={key} value={key}>{config.label}</option>
          ))}
        </select>

        <select
          value={filterStatus}
          onChange={(e) => setFilterStatus(e.target.value)}
          className="px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white text-sm focus:border-neon-cyan/50 focus:outline-none"
        >
          <option value="">All Statuses</option>
          {Object.entries(STATUS_CONFIG).map(([key, config]) => (
            <option key={key} value={key}>{config.label}</option>
          ))}
        </select>

        <select
          value={filterFacilityType}
          onChange={(e) => setFilterFacilityType(e.target.value)}
          className="px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white text-sm focus:border-neon-cyan/50 focus:outline-none"
        >
          <option value="">All Facility Types</option>
          {facilityTypes.map(type => (
            <option key={type} value={type}>{type}</option>
          ))}
        </select>

        <button
          onClick={fetchLeads}
          className="p-2 bg-white/5 border border-white/10 rounded-lg text-slate-400 hover:text-white hover:bg-white/10"
        >
          <RefreshCw className="w-4 h-4" />
        </button>
      </div>

      {/* Leads Table */}
      <div className="glass-card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-white/10 bg-white/5">
                <th className="w-10"></th>
                <th 
                  className="px-3 py-3 text-left text-xs font-medium text-slate-400 cursor-pointer hover:text-white"
                  onClick={() => toggleSort('company_name')}
                >
                  <div className="flex items-center space-x-1">
                    <span>Company</span>
                    {sortBy === 'company_name' && (sortOrder === 'asc' ? <SortAsc className="w-3 h-3" /> : <SortDesc className="w-3 h-3" />)}
                  </div>
                </th>
                <th className="px-3 py-3 text-left text-xs font-medium text-slate-400">Primary Contact</th>
                <th className="px-3 py-3 text-left text-xs font-medium text-slate-400">Location</th>
                <th 
                  className="px-3 py-3 text-left text-xs font-medium text-slate-400 cursor-pointer hover:text-white"
                  onClick={() => toggleSort('priority')}
                >
                  <div className="flex items-center space-x-1">
                    <span>Priority</span>
                    {sortBy === 'priority' && (sortOrder === 'asc' ? <SortAsc className="w-3 h-3" /> : <SortDesc className="w-3 h-3" />)}
                  </div>
                </th>
                <th className="px-3 py-3 text-left text-xs font-medium text-slate-400">Status</th>
                <th 
                  className="px-3 py-3 text-left text-xs font-medium text-slate-400 cursor-pointer hover:text-white"
                  onClick={() => toggleSort('last_contact_date')}
                >
                  <div className="flex items-center space-x-1">
                    <span>Last Contact</span>
                    {sortBy === 'last_contact_date' && (sortOrder === 'asc' ? <SortAsc className="w-3 h-3" /> : <SortDesc className="w-3 h-3" />)}
                  </div>
                </th>
                <th className="px-3 py-3 text-right text-xs font-medium text-slate-400">Actions</th>
              </tr>
            </thead>
            <tbody>
              {leads.length === 0 ? (
                <tr>
                  <td colSpan={8} className="px-4 py-12 text-center">
                    <Building2 className="w-12 h-12 mx-auto mb-4 text-slate-500 opacity-50" />
                    <p className="text-slate-400">No leads found</p>
                    <button
                      onClick={() => { setSelectedLead(null); setShowForm(true); }}
                      className="mt-2 text-neon-cyan hover:text-neon-cyan/80"
                    >
                      Add your first lead →
                    </button>
                  </td>
                </tr>
              ) : (
                leads.map(lead => (
                  <LeadRow
                    key={lead.id}
                    lead={lead}
                    expanded={expandedRows.has(lead.id)}
                    onToggle={toggleRow}
                    onView={(l) => { setSelectedLead(l); setShowForm(true); }}
                    onEdit={(l) => { setSelectedLead(l); setShowForm(true); }}
                    onDelete={handleDeleteLead}
                    onAssociations={(l) => { setAssociationLead(l); setShowAssociations(true); }}
                  />
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Lead Form Modal */}
      <AnimatePresence>
        {showForm && (
          <LeadFormModal
            isOpen={showForm}
            onClose={() => { setShowForm(false); setSelectedLead(null); }}
            lead={selectedLead}
            facilityTypes={facilityTypes}
            opportunityTypes={opportunityTypes}
            onSave={handleSaveLead}
          />
        )}
      </AnimatePresence>

      {/* Association Links Modal */}
      <AnimatePresence>
        {showAssociations && (
          <AssociationLinksModal
            isOpen={showAssociations}
            onClose={() => { setShowAssociations(false); setAssociationLead(null); }}
            lead={associationLead}
            allLeads={leads}
            onRefresh={fetchLeads}
          />
        )}
      </AnimatePresence>
    </div>
  );
};

export default LeadsCRM;
